/**
 * DCMCloud — Tooth Numbering Overlay
 * Interactive SVG dental chart supporting FDI (ISO 3950) and Universal notation.
 * Click a tooth to annotate it.
 */
import React, { useState, useCallback } from 'react';

const TEETH = [
  { fdi: '18', universal: 1,  quadrant: 1 }, { fdi: '17', universal: 2,  quadrant: 1 },
  { fdi: '16', universal: 3,  quadrant: 1 }, { fdi: '15', universal: 4,  quadrant: 1 },
  { fdi: '14', universal: 5,  quadrant: 1 }, { fdi: '13', universal: 6,  quadrant: 1 },
  { fdi: '12', universal: 7,  quadrant: 1 }, { fdi: '11', universal: 8,  quadrant: 1 },
  { fdi: '21', universal: 9,  quadrant: 2 }, { fdi: '22', universal: 10, quadrant: 2 },
  { fdi: '23', universal: 11, quadrant: 2 }, { fdi: '24', universal: 12, quadrant: 2 },
  { fdi: '25', universal: 13, quadrant: 2 }, { fdi: '26', universal: 14, quadrant: 2 },
  { fdi: '27', universal: 15, quadrant: 2 }, { fdi: '28', universal: 16, quadrant: 2 },
  { fdi: '38', universal: 17, quadrant: 3 }, { fdi: '37', universal: 18, quadrant: 3 },
  { fdi: '36', universal: 19, quadrant: 3 }, { fdi: '35', universal: 20, quadrant: 3 },
  { fdi: '34', universal: 21, quadrant: 3 }, { fdi: '33', universal: 22, quadrant: 3 },
  { fdi: '32', universal: 23, quadrant: 3 }, { fdi: '31', universal: 24, quadrant: 3 },
  { fdi: '41', universal: 25, quadrant: 4 }, { fdi: '42', universal: 26, quadrant: 4 },
  { fdi: '43', universal: 27, quadrant: 4 }, { fdi: '44', universal: 28, quadrant: 4 },
  { fdi: '45', universal: 29, quadrant: 4 }, { fdi: '46', universal: 30, quadrant: 4 },
  { fdi: '47', universal: 31, quadrant: 4 }, { fdi: '48', universal: 32, quadrant: 4 },
];

const STATUS_COLORS = {
  normal: '#374151', caries: '#ef4444', missing: '#6b7280',
  restored: '#3b82f6', crown: '#f59e0b', implant: '#10b981',
  endo: '#8b5cf6', fracture: '#f97316',
};
const STATUS_LABELS = {
  normal: 'Normal', caries: 'Caries', missing: 'Missing',
  restored: 'Restored', crown: 'Crown', implant: 'Implant',
  endo: 'Endo/RCT', fracture: 'Fracture',
};

export function ToothNumberingOverlay() {
  const [notation, setNotation] = useState('FDI');
  const [annotations, setAnnotations] = useState({});
  const [selected, setSelected] = useState(null);
  const [pendingStatus, setPendingStatus] = useState('normal');
  const [pendingNote, setPendingNote] = useState('');

  const toothId = (t) => notation === 'FDI' ? t.fdi : String(t.universal);
  const toothColor = (t) => {
    const ann = annotations[toothId(t)];
    return ann ? STATUS_COLORS[ann.status] : STATUS_COLORS.normal;
  };

  const handleClick = useCallback((t) => {
    const id = toothId(t);
    setSelected(id);
    const ex = annotations[id];
    setPendingStatus(ex?.status ?? 'normal');
    setPendingNote(ex?.note ?? '');
  }, [notation, annotations]);

  const save = () => {
    setAnnotations(prev => ({ ...prev, [selected]: { status: pendingStatus, note: pendingNote } }));
    setSelected(null);
  };

  const clear = () => {
    setAnnotations(prev => { const n = { ...prev }; delete n[selected]; return n; });
    setSelected(null);
  };

  const upper = TEETH.slice(0, 16);
  const lower = TEETH.slice(16, 32);
  const W = 22, H = 28, G = 2, P = 8;

  return (
    <div style={{ display:'flex', flexDirection:'column', height:'100%', background:'#1e1e2e', color:'#e2e8f0', fontFamily:'sans-serif', fontSize:'12px', overflowY:'auto' }}>
      <div style={{ padding:'8px 12px 4px', borderBottom:'1px solid #333', display:'flex', alignItems:'center', justifyContent:'space-between' }}>
        <span style={{ fontWeight:600, fontSize:'13px', color:'#10b981' }}>Tooth Chart</span>
        <div style={{ display:'flex', gap:'6px' }}>
          {['FDI','Universal'].map(n => (
            <button key={n} onClick={() => setNotation(n)} style={{ padding:'3px 10px', borderRadius:'10px', border:`1px solid ${notation===n?'#10b981':'#4b5563'}`, background:notation===n?'#10b98122':'transparent', color:notation===n?'#10b981':'#9ca3af', cursor:'pointer', fontSize:'11px', fontWeight:notation===n?600:400 }}>{n}</button>
          ))}
        </div>
      </div>

      <div style={{ padding:'8px 12px', overflowX:'auto' }}>
        <svg width={(W+G)*16+P*2} height={(H+G)*2+30+P*2} style={{ display:'block', margin:'0 auto' }}>
          <text x={(W+G)*8+P} y={12} textAnchor="middle" fill="#6b7280" fontSize="10">UPPER</text>
          {upper.map((t, i) => {
            const id = toothId(t);
            const x = P + i*(W+G), y = 16;
            return (
              <g key={t.fdi} onClick={() => handleClick(t)} style={{ cursor:'pointer' }}>
                <rect x={x} y={y} width={W} height={H} rx={4} fill={toothColor(t)} stroke={selected===id?'#fff':'#6b7280'} strokeWidth={selected===id?2:1} />
                <text x={x+W/2} y={y+H/2+4} textAnchor="middle" fill="#e2e8f0" fontSize="9" fontWeight={600}>{id}</text>
              </g>
            );
          })}
          <line x1={P} y1={16+H+G+4} x2={P+(W+G)*16-G} y2={16+H+G+4} stroke="#4b5563" strokeWidth={1} strokeDasharray="4 3"/>
          <text x={(W+G)*8+P} y={16+H+G*2+16} textAnchor="middle" fill="#6b7280" fontSize="10">LOWER</text>
          {lower.map((t, i) => {
            const id = toothId(t);
            const x = P + i*(W+G), y = 16+H+G*2+20;
            return (
              <g key={t.fdi} onClick={() => handleClick(t)} style={{ cursor:'pointer' }}>
                <rect x={x} y={y} width={W} height={H} rx={4} fill={toothColor(t)} stroke={selected===id?'#fff':'#6b7280'} strokeWidth={selected===id?2:1} />
                <text x={x+W/2} y={y+H/2+4} textAnchor="middle" fill="#e2e8f0" fontSize="9" fontWeight={600}>{id}</text>
              </g>
            );
          })}
        </svg>
      </div>

      <div style={{ padding:'4px 12px', display:'flex', flexWrap:'wrap', gap:'6px', borderBottom:'1px solid #333' }}>
        {Object.entries(STATUS_COLORS).map(([s, c]) => (
          <span key={s} style={{ display:'flex', alignItems:'center', gap:'4px', fontSize:'10px', color:'#9ca3af' }}>
            <span style={{ width:10, height:10, borderRadius:2, background:c, display:'inline-block' }}/>
            {STATUS_LABELS[s]}
          </span>
        ))}
      </div>

      {selected && (
        <div style={{ margin:'8px 12px', background:'#111827', borderRadius:'8px', padding:'10px', border:'1px solid #374151' }}>
          <div style={{ fontWeight:600, marginBottom:'8px', color:'#10b981' }}>Tooth {selected}</div>
          <label style={{ display:'block', color:'#9ca3af', fontSize:'10px', marginBottom:'4px' }}>STATUS</label>
          <select value={pendingStatus} onChange={e => setPendingStatus(e.target.value)} style={{ width:'100%', background:'#1f2937', color:'#e2e8f0', border:'1px solid #374151', borderRadius:'4px', padding:'5px 8px', fontSize:'12px', marginBottom:'8px' }}>
            {Object.entries(STATUS_LABELS).map(([s, l]) => <option key={s} value={s}>{l}</option>)}
          </select>
          <label style={{ display:'block', color:'#9ca3af', fontSize:'10px', marginBottom:'4px' }}>NOTE</label>
          <input type="text" value={pendingNote} onChange={e => setPendingNote(e.target.value)} placeholder="Optional clinical note" style={{ width:'100%', background:'#1f2937', color:'#e2e8f0', border:'1px solid #374151', borderRadius:'4px', padding:'5px 8px', fontSize:'12px', boxSizing:'border-box', marginBottom:'8px' }}/>
          <div style={{ display:'flex', gap:'6px' }}>
            <button onClick={save} style={{ flex:1, padding:'6px', borderRadius:'4px', background:'#10b981', color:'#fff', border:'none', cursor:'pointer', fontWeight:600, fontSize:'12px' }}>Save</button>
            <button onClick={clear} style={{ padding:'6px 10px', borderRadius:'4px', background:'transparent', color:'#ef4444', border:'1px solid #ef4444', cursor:'pointer', fontSize:'12px' }}>Clear</button>
            <button onClick={() => setSelected(null)} style={{ padding:'6px 10px', borderRadius:'4px', background:'transparent', color:'#9ca3af', border:'1px solid #374151', cursor:'pointer', fontSize:'12px' }}>Cancel</button>
          </div>
        </div>
      )}

      {Object.entries(annotations).filter(([,v]) => v.status !== 'normal').length > 0 && (
        <div style={{ padding:'8px 12px' }}>
          <div style={{ fontSize:'11px', color:'#9ca3af', textTransform:'uppercase', marginBottom:'6px' }}>
            Findings ({Object.entries(annotations).filter(([,v]) => v.status !== 'normal').length})
          </div>
          {Object.entries(annotations).filter(([,v]) => v.status !== 'normal').map(([id, ann]) => (
            <div key={id} style={{ display:'flex', alignItems:'center', gap:'8px', padding:'4px 0', borderBottom:'1px solid #1f2937' }}>
              <span style={{ width:10, height:10, borderRadius:2, background:STATUS_COLORS[ann.status], flexShrink:0 }}/>
              <span style={{ fontWeight:600, width:28 }}>{id}</span>
              <span style={{ color:STATUS_COLORS[ann.status] }}>{STATUS_LABELS[ann.status]}</span>
              {ann.note && <span style={{ color:'#9ca3af', fontSize:'11px' }}>— {ann.note}</span>}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default ToothNumberingOverlay;
